import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Modal, Form, Select, Input, Popconfirm, Space, Typography, message, DatePicker, Avatar } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const statusLabels = {
  active: { label: '生效中', color: 'red' },
  expired: { label: '已过期', color: 'default' },
  removed: { label: '已解除', color: 'green' },
};

const durationOptions = [
  { value: 'permanent', label: '永久' },
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
  { value: 90, label: '90 天' },
];

function UserBlacklistPage() {
  const [blacklist, setBlacklist] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();

  const loadBlacklist = async (p = 1) => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/user-blacklist', { params: { page: p, limit: 20 } });
      setBlacklist(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      message.error('加载黑名单失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBlacklist(); }, []);

  const handleSearchUser = async (keyword) => {
    if (!keyword || keyword.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { list } = await api.get('/admin/users', { params: { keyword, limit: 10 } });
      setSearchResults(list || []);
    } catch (e) {
      message.error('搜索用户失败');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({ user_id: user.id });
  };

  const handleAddToBlacklist = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        user_id: selectedUser?.id || values.user_id,
        reason: values.reason,
      };
      if (values.duration === 'permanent') {
        payload.expired_at = null;
      } else {
        payload.expired_at = dayjs().add(values.duration, 'day').format('YYYY-MM-DD HH:mm:ss');
      }
      await api.post('/admin/user-blacklist', payload);
      message.success('已加入黑名单');
      setAddModal(false);
      form.resetFields();
      setSelectedUser(null);
      setSearchResults([]);
      loadBlacklist(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('操作失败');
    }
  };

  const handleRemove = async (id) => {
    try {
      await api.delete(`/admin/user-blacklist/${id}`);
      message.success('已从黑名单移除');
      loadBlacklist(page);
    } catch (e) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用户信息', width: 250,
      render: (_, r) => (
        <Space>
          <Avatar size={32} src={r.user_avatar} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{r.user_nickname || '-'}</Text></div>
            <div><Text type="secondary">{r.user_phone || '-'}</Text></div>
          </div>
        </Space>
      ),
    },
    { title: '拉黑原因', dataIndex: 'reason', ellipsis: true },
    { title: '操作人', dataIndex: 'operator_name', width: 120, render: (v) => v || '-' },
    {
      title: '到期时间', dataIndex: 'expired_at', width: 180,
      render: (v) => v ? new Date(v).toLocaleString() : <Tag color="red">永久</Tag>,
    },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (v) => <Tag color={statusLabels[v]?.color}>{statusLabels[v]?.label || v}</Tag>,
    },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 120,
      render: (_, r) => (
        r.status === 'active' ? (
          <Popconfirm title="确定将该用户从黑名单移除?" onConfirm={() => handleRemove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>移除</Button>
          </Popconfirm>
        ) : null
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>黑名单管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setSelectedUser(null); setSearchResults([]); setAddModal(true); }} style={{ background: '#07C160' }}>
          加入黑名单
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={blacklist}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: loadBlacklist,
            showSizeChanger: false,
          }}
          size="small"
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 加入黑名单 Modal */}
      <Modal title="加入黑名单" open={addModal} onOk={handleAddToBlacklist} onCancel={() => setAddModal(false)} width={550}>
        <Form form={form} layout="vertical">
          <Form.Item label="搜索用户">
            <Select
              showSearch
              placeholder="输入昵称或手机号搜索用户"
              searchValue={form.getFieldValue('keyword')}
              onSearch={handleSearchUser}
              loading={searching}
              options={searchResults.map(u => ({
                value: u.id,
                label: (
                  <Space>
                    <Avatar size={24} src={u.avatar} icon={<UserOutlined />} />
                    <span>{u.nickname}</span>
                    <Text type="secondary">{u.phone}</Text>
                  </Space>
                ),
              }))}
              onSelect={(val) => {
                const user = searchResults.find(u => u.id === val);
                handleSelectUser(user);
              }}
              filterOption={false}
              notFoundContent={searching ? '搜索中...' : '请输入关键词搜索'}
            />
          </Form.Item>
          {selectedUser && (
            <Form.Item label="已选用户">
              <Space>
                <Avatar src={selectedUser.avatar} icon={<UserOutlined />} />
                <Text strong>{selectedUser.nickname}</Text>
                <Text type="secondary">{selectedUser.phone}</Text>
              </Space>
            </Form.Item>
          )}
          <Form.Item name="reason" label="拉黑原因" rules={[{ required: true, message: '请输入拉黑原因' }]}>
            <Input.TextArea rows={3} placeholder="请输入拉黑原因" maxLength={200} showCount />
          </Form.Item>
          <Form.Item name="duration" label="封禁时长" rules={[{ required: true, message: '请选择封禁时长' }]} initialValue="permanent">
            <Select placeholder="选择封禁时长">
              {durationOptions.map(o => (
                <Select.Option key={String(o.value)} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default UserBlacklistPage;
